"use client";

// Requirements summary:
// - Search indexing uses the shared normalized schedule event type so calendar
//   helper modules do not depend on the ScheduleView component export surface.

import { useMemo, useCallback } from 'react';
import type { NormalizedEvent } from '@/utils/schedule/types';
import { Team } from '@/utils/types/team';
import { createLogger } from '@/utils/logger/devLogger';

const searchLogger = createLogger('SearchIndex');

export interface SearchableEvent {
  id: string;
  title: string;
  description?: string;
  eventType?: string;
  locationName?: string;
  teamName?: string;
  assignedTeams?: Array<{ name: string }>;
  venue_name?: string;
  startDate: string;
  endDate: string;
  isAllDay: boolean;
  /** The gym's timezone for this event, so the dropdown can print gym time (#3611). */
  timezone?: string;
  searchText: string;
  category: 'event';
}

export interface SearchableTeam {
  id: string;
  name: string;
  gymName?: string;
  searchText: string;
  category: 'team';
}

export interface SearchableAthlete {
  id: string;
  firstName: string;
  lastName: string;
  teams?: string[];
  searchText: string;
  category: 'athlete';
}

export interface SearchableLocation {
  id: string;
  name: string;
  address?: string;
  searchText: string;
  category: 'location';
}

export type SearchResult = SearchableEvent | SearchableTeam | SearchableAthlete | SearchableLocation;

export interface TypeaheadResult {
  items: SearchResult[];
  totalCount: number;
  categories: {
    events: SearchableEvent[];
    teams: SearchableTeam[];
    athletes: SearchableAthlete[];
    locations: SearchableLocation[];
  };
}

interface UseSearchIndexProps {
  events?: NormalizedEvent[];
  teams?: Team[];
  athletes?: any[];
  locations?: any[];
  enabled?: boolean;
}

export const useSearchIndex = ({
  events = [],
  teams = [],
  athletes = [],
  locations = [],
  enabled = true,
}: UseSearchIndexProps) => {

  // Create search index for events
  const searchableEvents = useMemo((): SearchableEvent[] => {
    if (!enabled) return [];
    return events.map(event => {
      const teamNames = event.assignedTeams?.map(team => team.name).filter(Boolean) || [];
      if (event.teamName && event.teamName !== 'Unknown Team' && event.teamName !== 'No team assigned') {
        teamNames.push(event.teamName);
      }

      const searchableFields = [
        event.title,
        event.description,
        event.eventType,
        event.locationName,
        event.venue_name,
        ...teamNames
      ].filter(Boolean);

      return {
        id: event.id,
        title: event.title,
        description: event.description,
        eventType: event.eventType,
        locationName: event.locationName,
        teamName: event.teamName,
        assignedTeams: event.assignedTeams,
        venue_name: event.venue_name,
        startDate: event.startDate,
        endDate: event.endDate,
        isAllDay: event.isAllDay || false,
        timezone: event.timezone,
        searchText: searchableFields.join(' ').toLowerCase(),
        category: 'event' as const
      };
    });
  }, [events]);

  // Create search index for teams
  const searchableTeams = useMemo((): SearchableTeam[] => {
    if (!enabled) return [];
    return teams.map(team => ({
      id: team.id,
      name: team.name,
      gymName: undefined, // TODO: Add gym name from gym data when available
      searchText: `${team.name} ${team.level || ''} ${team.age_division || ''}`.toLowerCase(),
      category: 'team' as const
    }));
  }, [teams, enabled]);

  // Create search index for athletes
  const searchableAthletes = useMemo((): SearchableAthlete[] => {
    if (!enabled) return [];
    return athletes.map(athlete => ({
      id: athlete.id,
      firstName: athlete.first_name,
      lastName: athlete.last_name,
      teams: athlete.teams?.map((t: any) => t.name),
      searchText: `${athlete.first_name} ${athlete.last_name} ${athlete.teams?.map((t: any) => t.name).join(' ') || ''}`.toLowerCase(),
      category: 'athlete' as const
    }));
  }, [athletes, enabled]);

  // Create search index for locations
  const searchableLocations = useMemo((): SearchableLocation[] => {
    if (!enabled) return [];
    return locations.map(location => ({
      id: location.id,
      name: location.name,
      address: location.address,
      searchText: `${location.name} ${location.address || ''}`.toLowerCase(),
      category: 'location' as const
    }));
  }, [locations, enabled]);

  // Fast search function for typeahead
  const instantSearch = useCallback((query: string, maxResultsPerCategory: number = 3): TypeaheadResult => {
    if (!enabled) {
      return {
        items: [],
        totalCount: 0,
        categories: {
          events: [],
          teams: [],
          athletes: [],
          locations: []
        }
      };
    }
    if (!query || query.length < 2) {
      return {
        items: [],
        totalCount: 0,
        categories: {
          events: [],
          teams: [],
          athletes: [],
          locations: []
        }
      };
    }

    const lowerQuery = query.toLowerCase();
    const startTime = performance.now();

    // Search each category
    const matchedEvents = searchableEvents
      .filter(item => item.searchText.includes(lowerQuery))
      .slice(0, maxResultsPerCategory);

    const matchedTeams = searchableTeams
      .filter(item => item.searchText.includes(lowerQuery))
      .slice(0, maxResultsPerCategory);

    const matchedAthletes = searchableAthletes
      .filter(item => item.searchText.includes(lowerQuery))
      .slice(0, maxResultsPerCategory);

    const matchedLocations = searchableLocations
      .filter(item => item.searchText.includes(lowerQuery))
      .slice(0, maxResultsPerCategory);

    const allResults: SearchResult[] = [
      ...matchedEvents,
      ...matchedTeams,
      ...matchedAthletes,
      ...matchedLocations
    ];

    const endTime = performance.now();
    searchLogger.debug('Instant search completed:', {
      query,
      resultsCount: allResults.length,
      searchTime: `${(endTime - startTime).toFixed(2)}ms`,
      breakdown: {
        events: matchedEvents.length,
        teams: matchedTeams.length,
        athletes: matchedAthletes.length,
        locations: matchedLocations.length
      }
    });

    return {
      items: allResults,
      totalCount: allResults.length,
      categories: {
        events: matchedEvents,
        teams: matchedTeams,
        athletes: matchedAthletes,
        locations: matchedLocations
      }
    };
  }, [enabled, searchableEvents, searchableTeams, searchableAthletes, searchableLocations]);

  // Smart search with scoring (for more sophisticated results)
  const smartSearch = useCallback((query: string): SearchResult[] => {
    if (!enabled) return [];
    if (!query || query.length < 2) return [];

    const lowerQuery = query.toLowerCase();
    const results: Array<SearchResult & { score: number }> = [];

    // Search and score results
    [...searchableEvents, ...searchableTeams, ...searchableAthletes, ...searchableLocations].forEach(item => {
      let score = 0;

      // Title/name exact match gets highest score
      const primaryField = item.category === 'event' ? item.title :
                          item.category === 'athlete' ? `${item.firstName} ${item.lastName}` :
                          item.name;

      if (primaryField.toLowerCase() === lowerQuery) {
        score += 100;
      } else if (primaryField.toLowerCase().startsWith(lowerQuery)) {
        score += 50;
      } else if (primaryField.toLowerCase().includes(lowerQuery)) {
        score += 25;
      }

      // Secondary fields get lower scores
      if (item.searchText.includes(lowerQuery)) {
        score += 10;
      }

      if (score > 0) {
        results.push({ ...item, score });
      }
    });

    // Sort by score and return
    return results
      .sort((a, b) => b.score - a.score)
      .map(({ score, ...item }) => item);
  }, [enabled, searchableEvents, searchableTeams, searchableAthletes, searchableLocations]);

  return {
    // Search functions
    instantSearch,
    smartSearch,

    // Index data for external use
    searchableEvents,
    searchableTeams,
    searchableAthletes,
    searchableLocations,

    // Utilities
    getTotalIndexSize: useCallback(() =>
      searchableEvents.length + searchableTeams.length + searchableAthletes.length + searchableLocations.length,
      [searchableEvents.length, searchableTeams.length, searchableAthletes.length, searchableLocations.length]
    )
  };
};
